/**
 * WebViewJavascriptBridge 接口定义
 */
declare interface WebViewJavascriptBridge {
  callHandler(
    eventName: string,
    data: any,
    callback?: (response: any) => void,
  ): void
  registerHandler(
    eventName: string,
    callback: (data: any, responseCallback?: (response: any) => void) => void,
  ): void
}

/**
 * 全局 window 接口扩展
 */
declare global {
  interface Window {
    WebViewJavascriptBridge?: WebViewJavascriptBridge
    WVJBCallbacks?: ((bridge: WebViewJavascriptBridge) => void)[]
    bridge?: Record<
      string,
      (data: any, responseCallback?: (response: any) => void) => void
    >
    cefQuery?: (options: {
      request: string
      onSuccess: (res: any) => void
      onFailure: (res: any) => void
    }) => void
    webkit?: {
      messageHandlers?: {
        native?: {
          postMessage: (message: {
            action: string
            data: any
          }) => void
        }
      }
    }
  }
}

/**
 * 建立与 WebViewJavascriptBridge 的连接（主要用于 Android）
 * @param callback 连接成功后的回调函数
 */
export function connectWebViewJavascriptBridge(
  callback: (bridge: WebViewJavascriptBridge) => void,
): void {
  if (window.WebViewJavascriptBridge) {
    callback(window.WebViewJavascriptBridge)
  } else {
    document.addEventListener(
      'WebViewJavascriptBridgeReady',
      () => {
        callback(window.WebViewJavascriptBridge!)
      },
      false,
    )
    const WVJBIframe = document.createElement('iframe')
    WVJBIframe.style.display = 'none'
    WVJBIframe.src = 'memeshot://__bridge_loaded__'
    document.documentElement.appendChild(WVJBIframe)
    setTimeout(() => {
      document.documentElement.removeChild(WVJBIframe)
    }, 0)
  }
}

/**
 * 设置 WebViewJavascriptBridge（主要用于 iOS）
 * @param callback 设置成功后的回调函数
 */
export function setupWebViewJavascriptBridge(
  callback: (bridge: WebViewJavascriptBridge) => void,
): void {
  if (window.WebViewJavascriptBridge) {
    callback(window.WebViewJavascriptBridge)
    return
  }
  if (window.WVJBCallbacks) {
    window.WVJBCallbacks.push(callback)
    return
  }
  const WVJBIframe = document.createElement('iframe')
  WVJBIframe.style.display = 'none'
  WVJBIframe.src = 'https://__bridge_loaded__'
  document.documentElement.appendChild(WVJBIframe)
  setTimeout(() => {
    document.documentElement.removeChild(WVJBIframe)
  }, 20)
}

/**
 * 调用原生应用的方法
 * @param eventName 要调用的原生方法名称
 * @param requestData 传递给原生方法的数据
 * @param callback 原生方法执行完成后的回调函数
 */
export function callHandler(
  eventName: string,
  requestData: Record<string, any> = {},
  callback?: (result: { code: number; error: string; data: any }) => void,
): void {
  // 确保 requestData 是对象并添加 timestamp 字段
  requestData.timestamp = new Date().getTime()

  if (window.cefQuery) {
    // Windows 平台使用 cefQuery
    window.cefQuery({
      request: `${eventName}:${JSON.stringify(requestData)}`,
      onSuccess(res) {
        const params = { code: 0, error: 'success', data: res }
        callback?.(params)
      },
      onFailure(res) {
        const params = { code: 1, error: 'fail', data: res }
        callback?.(params)
      },
    })
  } else if (window.navigator.userAgent.indexOf('Android') > -1) {
    // Android 平台
    connectWebViewJavascriptBridge((bridge) => {
      bridge.callHandler(eventName, requestData, callback)
    })
  } else if (window.webkit?.messageHandlers?.native) {
    // 新的 iOS 实现方式
    console.log('Using new iOS WKWebView message handler')
    const message = {
      action: eventName,
      data: requestData
    }
    window.webkit.messageHandlers.native.postMessage(message)
    // 注意：新的实现方式可能不支持回调，需要 iOS 端通过其他方式返回结果
    if (callback) {
      console.log('Callback provided but new iOS implementation may not support it')
    }
  } else {
    // iOS 平台
    setupWebViewJavascriptBridge((bridge) => {
      bridge.callHandler(eventName, requestData, callback)
    })
  }
}

/**
 * 检测当前运行平台
 * @returns 平台名称 ('ipad' | 'mac' | 'windows' | 'unknown')
 */
export function platform(): 'ipad' | 'mac' | 'windows' | 'unknown' {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.indexOf('ipad') > -1 || ua.indexOf('android') > -1) {
    return 'ipad'
  }
  if (ua.indexOf('macintosh') > -1) {
    return 'mac'
  }
  if (ua.indexOf('windows') > -1) {
    return 'windows'
  }
  return 'unknown'
}

/**
 * 注册事件处理器，用于接收原生应用的调用
 * @param eventName 事件名称
 * @param callback 事件处理函数
 */
export function registerHandler(
  eventName: string,
  callback: (data: any) => void,
): void {
  const cb = function (
    data: any,
    // responseCallback?: (response: any) => void,
  ): void {
    callback && callback(data)
    const now = new Date().getTime()
    if (data && data.timestamp) {
      console.log(
        `bridge: [eventName]~${eventName}}, [time]~${now - data.timestamp}}`,
      )
    }
  }

  if (window.cefQuery) {
    // Windows 平台
    window.bridge = window.bridge || {}
    window.bridge[eventName] = cb
  } else if (window.navigator.userAgent.indexOf('Android') > -1) {
    // Android 平台
    connectWebViewJavascriptBridge((bridge) => {
      bridge.registerHandler(eventName, cb)
    })
  } else {
    // iOS 平台
    setupWebViewJavascriptBridge((bridge) => {
      bridge.registerHandler(eventName, cb)
    })
  }
}

// 导出默认对象，保持与原 JS 文件的兼容性
export default {
  callHandler,
  registerHandler,
  platform,
}
